<template>
  <Transition name="open-in-app">
    <aside v-if="isVisible" class="open-in-app" aria-label="Открыть страницу в приложении Marso">
      <button
        class="open-in-app__close"
        type="button"
        aria-label="Закрыть предложение"
        @click="dismiss"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.25 19.25L5 5"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M19.25 5L5 19.25"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div class="open-in-app__content">
        <strong class="open-in-app__title">Открыть в приложении?</strong>
        <span class="open-in-app__description">Продолжите работу в приложении Marso</span>
      </div>

      <a class="open-in-app__action" :href="intentUrl" rel="nofollow">Открыть</a>
    </aside>
  </Transition>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  buildAndroidAppIntentUrl,
  getAndroidAppTarget,
  isAndroidUserAgent,
} from '@/utils/androidAppLink.js'

const route = useRoute()

const dismissed = ref(false)
const isAppInstalled = ref(false)

const intentUrl = computed(() => {
  // Нужна зависимость от route, чтобы ссылка обновлялась
  // при навигации внутри SPA.
  void route.fullPath

  return buildAndroidAppIntentUrl(window.location.href)
})

const isVisible = computed(() => {
  return (
    !dismissed.value &&
    isAppInstalled.value &&
    Boolean(intentUrl.value)
  )
})

onMounted(async () => {
  if (!isAndroidUserAgent(navigator.userAgent)) {
    return
  }

  const target = getAndroidAppTarget(window.location.href)

  if (!target) {
    return
  }

  if (typeof navigator.getInstalledRelatedApps !== 'function') {
    return
  }

  try {
    const installedApps = await navigator.getInstalledRelatedApps()

    isAppInstalled.value = installedApps.some(
      (app) =>
        app.platform === 'play' &&
        app.id === target.packageName,
    )
  } catch (error) {
    console.error('Failed to detect installed Android app', error)
    isAppInstalled.value = false
  }
})

function dismiss() {
  dismissed.value = true
}
</script>

<style scoped>
.open-in-app {
  position: fixed;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom));
  left: 12px;
  z-index: 10000;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  max-width: 520px;
  margin: 0 auto;
  padding: 16px;
  border: 1px solid var(--m-color-border-light);
  border-radius: 16px;
  background: var(--m-color-white);
  box-shadow: var(--m-shadow-m);
  color: var(--m-color-text-main);
}

.open-in-app__close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  padding: 0;
  color: var(--m-color-text-secondary);
  font-size: 24px;
  line-height: 32px;
}

.open-in-app__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.open-in-app__title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.25;
}

.open-in-app__description {
  margin-top: 4px;
  color: var(--m-color-text-secondary);
  font-size: 13px;
  line-height: 1.35;
}

.open-in-app__action {
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 10px 18px;
  border-radius: var(--m-radius-l);
  background: var(--m-color-primary);
  color: var(--m-color-text-white);
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
}

.open-in-app-enter-active,
.open-in-app-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.open-in-app-enter-from,
.open-in-app-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 760px) {
  .open-in-app {
    grid-template-columns: 1fr;
  }

  .open-in-app__action {
    width: 100%;
  }
}
</style>
